<template>
    <div>
        <div v-if="formSection==`Drug`">
            <card title="Brand Record">
                <form>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.ndc"
                                      :disabled="disableFields"
                                      :required="true"
                                      label="NDC"
                                      type="string"
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.name"
                                      :disabled="disableFields"
                                      label="Name"
                                      required
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.labelName"
                                      :disabled="disableFields"
                                      label="Label Name">
                                <template #label>
                                    Label Name
                                    <small class="text-muted">(Used for printing labels and documents. Falls back to Name if not specified.)</small>
                                </template>
                            </fg-input>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col cols="3">
                            <fg-input v-model.number="drug.awp"
                                      :disabled="disableFields"
                                      :no-wheel="true"
                                      label="AWP"
                                      placeholder="$0.00"
                                      type="number">
                            </fg-input>
                        </b-col>
                        <b-col cols="3">
                            <fg-input v-model.number="drug.packageSize"
                                      :disabled="disableFields"
                                      :no-wheel="true"
                                      label="Package Size"
                                      type="number"
                                      required>
                            </fg-input>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.unitOfMeasure"
                                      :disabled="disableFields"
                                      label="Unit of Measure"
                                      required
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col cols="5">
                            <fg-input v-model.number="drug.minimumDispenseQuantity"
                                      :disabled="disableFields"
                                      :no-wheel="true"
                                      label="Minimum Dispense Quantity"
                                      required
                                      type="number">
                            </fg-input>
                        </b-col>
                        <b-col cols="3">
                            <fg-input v-model.number="drug.deaClass"
                                      :disabled="disableFields"
                                      label="DEA Class"
                                      type="number"/>
                        </b-col>
                        <b-col cols="4">
                            <fg-input v-model.number="drug.auditChance"
                                      :disabled="disableFields"
                                      :no-wheel="true"
                                      label="Audit Chance (%)"
                                      type="number"
                                      min="0"
                                      max="100"
                                      placeholder="0-100">
                            </fg-input>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.instructions"
                                      :disabled="disableFields"
                                      label="Instructions"

                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <EnumSelector v-model="drug.dosageForm"
                                          :disabled="disableFields"
                                          :enum-codes="dosageForms"
                                          label="Dosage Form">
                                <template #label>
                                    Dosage Form
                                    <small class="text-muted">(Used for NCPDP billing claims)</small>
                                </template>
                            </EnumSelector>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.routeOfAdministration"
                                      :disabled="disableFields"
                                      label="Route of Administration">
                                <template #label>
                                    Route of Administration
                                    <small class="text-muted">(NCPDP E2 field, max 11 characters)</small>
                                </template>
                            </fg-input>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <EnumSelector v-model="drug.compoundType"
                                          :disabled="disableFields"
                                          :enum-codes="compoundTypes"
                                          label="Compound Type">
                                <template #label>
                                    Compound Type
                                    <small class="text-muted">(NCPDP G1 field - sent based on PBM configuration)</small>
                                </template>
                            </EnumSelector>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <EnumSelector v-model="drug.levelOfEffort"
                                          :clearable="true"
                                          :disabled="disableFields"
                                          :enum-codes="levelOfEffortCodes"
                                          :display-names="levelOfEffortDisplayNames"
                                          label="Level of Effort">
                                <template #label>
                                    Level of Effort
                                    <small class="text-muted">(NCPDP 474-8E field - auto-applied to billing)</small>
                                </template>
                            </EnumSelector>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <EnumSelector v-model="drug.status"
                                          :disabled="disableFields"
                                          :enum-codes="statuses"
                                          label="Status"
                                          required></EnumSelector>
                        </b-col>
                    </b-form-row>
                    <hr>
                </form>
                <b-col cols="12">
                    <b-button variant="success" @click="continueClicked">Continue</b-button>
                    <b-button @click="backToCompoundDashboard">Cancel</b-button>
                </b-col>
            </card>
        </div>

        <div v-if="formSection==`PatEd`">
            <card title="Patient Education Record">
                <form>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.patEd.description"
                                      :disabled="disableFields"
                                      label="Description"
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.patEd.contraindications"
                                      :disabled="disableFields"
                                      label="Contraindications"
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.patEd.administration"
                                      :disabled="disableFields"
                                      label="Administration"
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.patEd.missedDoseInstructions"
                                      :disabled="disableFields"
                                      label="Missed Dose Instructions"
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.patEd.monitoring"
                                      :disabled="disableFields"
                                      label="Monitoring"
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.patEd.sideEffects"
                                      :disabled="disableFields"
                                      label="Side Effects"
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.patEd.storage"
                                      :disabled="disableFields"
                                      label="Storage"
                            />
                        </b-col>
                    </b-form-row>
                </form>
                <b-col cols="12">
                    <b-button variant="success" @click="continueClicked">Continue</b-button>
                    <b-button @click="backToCompoundDashboard">Cancel</b-button>
                </b-col>
            </card>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Mixins, Prop } from 'vue-property-decorator';
    import DisableAutocompleteMixin from '@/mixins/DisableAutocompleteMixin';
    import { CompoundDrug, CompoundDosageForm, CompoundType, CompoundLevelOfEffort, CompoundLevelOfEffortDisplayNames } from '@/models/Drug/CompoundDrug';
    import EnumSelector from '@/components/EnumSelector.vue';
    import Checkbox from "@/components/Inputs/Checkbox.vue";
    import { DrugStatus } from "@/models/Drug/Drug";

    @Component({
        name: "CompoundFormComponent",
        components: {
            EnumSelector,
            Checkbox
        }
    })
    export default class CompoundFormComponent extends Mixins(DisableAutocompleteMixin) {
        @Prop() private drug!: CompoundDrug;
        @Prop() private formSection!: string;
        private statuses = DrugStatus.DrugStatusEnum;
        private dosageForms = CompoundDosageForm;
        private compoundTypes = CompoundType;
        private levelOfEffortCodes = CompoundLevelOfEffort;
        private levelOfEffortDisplayNames = CompoundLevelOfEffortDisplayNames;


        get disableFields(): boolean {
            return false
        }

        created() {
            //console.log("%c Created NonGSDDFormComponent", "color: green");
        }

        continueClicked() {
            if (this.drug.instructions == null) this.drug.instructions = "";
            if (this.drug.ndc == null || this.drug.name == null || this.drug.packageSize == null || this.drug.unitOfMeasure == null ||
                this.drug.minimumDispenseQuantity == null ||
                this.drug.ndc.trim() == "" || this.drug.name.trim() == "" || this.drug.unitOfMeasure.trim() == "" ||
                this.drug.minimumDispenseQuantity < 0.0001) {
                this.$bvModal.msgBoxOk(`Required Fields: NDC, Name, Package Size ,Unit of Measure, Minimum Dispense Quantity - can not be left empty`);
                return
            }
            this.$emit('continue');
        }

        backToCompoundDashboard() {
            this.$emit("close");
            return;
        }
    }
</script>

<style scoped>
</style>
